import { Factory, Wrench, Users } from "lucide-react";
import { COMPANY, MACHINERY, TEAM_ROLES } from "@/lib/data";

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="py-24 md:py-32 bg-ink text-bone relative overflow-hidden grain"
    >
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-20">
          <div className="md:col-span-7">
            <span className="tech-rule">04 — Capabilities</span>
            <h2
              data-testid="capabilities-heading"
              className="mt-6 font-heading font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tighter"
            >
              Owned plant.<br />
              <span className="italic font-medium text-rust">Trained crew.</span><br />
              In-house machinery.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-bone/70 leading-relaxed self-end">
            Full-stack execution: from our own asphalt plant in Jigani to a fleet
            of compactors, pavers and excavators — supervised by an experienced
            engineering team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-bone/15">
          {/* Plant */}
          <div className="lg:col-span-5 p-8 md:p-10 bg-ink relative overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              <img
                src="https://images.unsplash.com/photo-1772852340517-b11ec88979fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxhc3BoYWx0JTIwcm9hZCUyMGNvbnN0cnVjdGlvbiUyMHBhdmVyJTIwbWFjaGluZXxlbnwwfHx8fDE3NzkzNTM4NjZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Asphalt plant"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-ink via-ink/80 to-transparent" />
            </div>
            <div className="relative">
              <div className="inline-flex items-center justify-center w-12 h-12 border border-bone mb-6">
                <Factory className="w-5 h-5" strokeWidth={2} />
              </div>
              <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-rust mb-3">
                Owned Plant
              </div>
              <h3 className="font-heading font-bold text-3xl md:text-4xl tracking-tight leading-[0.95] mb-4">
                Asphalt Plant<br/>at Jigani Hobli
              </h3>
              <p className="text-bone/75 text-sm leading-relaxed mb-6 max-w-md">
                {COMPANY.plant.address}
              </p>
              <div className="font-mono text-xs tracking-widest uppercase text-bone/55">
                Direct supply · No 3rd-party dependency
              </div>
            </div>
          </div>

          {/* Machinery */}
          <div className="lg:col-span-4 p-8 md:p-10 bg-ink">
            <div className="inline-flex items-center justify-center w-12 h-12 border border-bone mb-6">
              <Wrench className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-rust mb-3">
              Machinery & Equipment
            </div>
            <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight mb-6">
              In-house fleet.
            </h3>
            <ul className="divide-y divide-bone/15 border-y border-bone/15">
              {MACHINERY.map((m, i) => (
                <li
                  key={m}
                  data-testid={`machinery-${i}`}
                  className="py-3 flex items-center justify-between text-bone/85"
                >
                  <span className="font-medium text-base">{m}</span>
                  <span className="font-mono text-[10px] tracking-widest text-bone/40">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Team */}
          <div className="lg:col-span-3 p-8 md:p-10 bg-ink">
            <div className="inline-flex items-center justify-center w-12 h-12 border border-bone mb-6">
              <Users className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-rust mb-3">
              Engineering Team
            </div>
            <h3 className="font-heading font-bold text-2xl md:text-3xl tracking-tight leading-tight mb-6">
              Skilled hands.<br />Sharp minds.
            </h3>
            <ul className="space-y-3">
              {TEAM_ROLES.map((r) => (
                <li
                  key={r}
                  data-testid={`team-role-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  className="flex items-center gap-3 text-bone/85"
                >
                  <span className="w-1.5 h-1.5 bg-rust" />
                  <span className="text-sm font-medium">{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
